import 'leaflet/dist/leaflet.css';

import classNames from 'classnames';
import L, { LatLngExpression } from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { PropsWithChildren, useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';

import { AddressValue } from '@client/api';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconAnchor: [13, 41],
  popupAnchor: [0, -34],
  shadowUrl: iconShadow,
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapProps {
  className?: string;
  address?: AddressValue;
  zoom?: number;
  scrollWheelZoom?: boolean;
  markerPosition?: LatLngExpression;
}

function RefreshMap({ center, zoom }: { center: LatLngExpression; zoom: number }) {
  const map = useMap();
  map.setView(center, zoom);
  return null;
}

export const Map = (props: PropsWithChildren<MapProps>) => {
  const { className = 'h-[400]', address, zoom = 15, scrollWheelZoom = false, markerPosition, children } = props;

  const { latitude, longitude } = useMemo(
    () => ({ latitude: address?.coordinate?.latitude, longitude: address?.coordinate?.longitude }),
    [address]
  );

  const position = useMemo<LatLngExpression | null>(
    () => (latitude && longitude ? [latitude, longitude] : null),
    [latitude, longitude]
  );

  return (
    <div className={classNames('bg-neutral-100', className)}>
      {position ? (
        <MapContainer center={position} zoom={zoom} scrollWheelZoom={scrollWheelZoom} className={classNames('z-0', className)}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={markerPosition || position}>
            {children && <Popup closeButton={false}>{children}</Popup>}
          </Marker>
          <RefreshMap center={position} zoom={zoom} />
        </MapContainer>
      ) : (
        <span className="h-full justify-center text-center items-center flex font-normal text-sm text-neutral-600">No coordinates found</span>
      )}
    </div>
  );
};

export default Map;
